import type { Plan } from '@obv/api-client';
import { comparePlans, formatTime, matchesFilter, monthGrid, weekDays, weekdayHeaders } from '@obv/core';
import { personColor } from '@obv/ui';
import { useChoreData } from '../choreData';
import { memberLabel, useDisplay } from '../context';
import { useDisplayUi } from '../store';
import { Card, Unavailable } from '../ui';

interface GridProps {
  anchor: string;
  plans: Plan[];
  loading: boolean;
  onOpenDay: (date: string) => void;
}

function useDayIndex(plans: Plan[]) {
  const filter = useDisplayUi((s) => s.filter);
  const { tasks } = useChoreData();
  const byDay: Record<string, Plan[]> = {};
  for (const p of plans) {
    if (p.deleted_at || !matchesFilter(p, filter)) continue;
    (byDay[p.date] = byDay[p.date] || []).push(p);
  }
  for (const k of Object.keys(byDay)) byDay[k]!.sort(comparePlans);
  const chores: Record<string, number> = {};
  if (tasks.data) {
    for (const t of tasks.data.items) {
      if (!t.completed && t.due_date && matchesFilter(t, filter)) chores[t.due_date] = (chores[t.due_date] || 0) + 1;
    }
  }
  return { byDay, chores };
}

const choreLabel = (n: number) => `${n} ${n === 1 ? 'chore' : 'chores'}`;

/** Seven columns: each day's plans in time order; tap a column to open that day. */
export function WeekView({ anchor, plans, loading, onOpenDay }: GridProps) {
  const ctx = useDisplay();
  const today = ctx.clock.dateKey;
  const days = weekDays(anchor, ctx.prefs.week_starts);
  const heads = weekdayHeaders(ctx.prefs.week_starts);
  const { byDay, chores } = useDayIndex(plans);
  if (loading) return <Card tone={5} className="mt-[14px] flex-1"><Unavailable>Loading the week…</Unavailable></Card>;
  return (
    <Card tone={5} className="mt-[14px] grid min-h-0 flex-1 overflow-hidden" style={{ gridTemplateColumns: 'repeat(7, minmax(0,1fr))' }}>
      {days.map((d, i) => {
        const isToday = d === today;
        const list = byDay[d] || [];
        return (
          <button
            key={d}
            type="button"
            onClick={() => onOpenDay(d)}
            aria-label={`${heads[i]} ${Number(d.slice(8))}: ${list.length} plans. Open day.`}
            className="tap flex min-w-0 flex-col items-stretch text-left"
            style={{ borderLeft: i ? '1px solid var(--line2)' : 'none' }}
          >
            <div className="flex flex-none flex-col items-center border-b border-line py-2">
              <span className="text-[12px] uppercase tracking-[0.08em] text-muted">{heads[i]}</span>
              <span
                className="mt-1 flex h-[30px] w-[30px] items-center justify-center rounded-full text-[16px]"
                style={isToday ? { background: 'var(--ink)', color: 'var(--onInk)', fontWeight: 600 } : undefined}
              >
                {Number(d.slice(8))}
              </span>
            </div>
            <div className="min-h-0 flex-1 overflow-hidden px-[6px] pt-1">
              {list.map((p) => (
                <div key={p.id} className="mt-1 flex items-start" style={{ opacity: p.completed ? 0.5 : 1 }}>
                  <span className="mr-[5px] mt-[3px] h-[14px] w-[3px] flex-none rounded-[2px]" style={{ background: personColor(memberLabel(ctx, p.member_id).colorKey) }} />
                  <span className="min-w-0 text-[12px] leading-[1.3]">
                    <span className="block text-muted">{p.all_day ? 'All day' : formatTime(p.time!, ctx.prefs.h24)}</span>
                    <span className="block" style={{ textDecoration: p.completed ? 'line-through' : 'none', overflowWrap: 'break-word' }}>
                      {p.title}
                    </span>
                  </span>
                </div>
              ))}
            </div>
            {chores[d] ? <div className="flex-none px-[6px] pb-2 pt-1 text-[12px] text-muted">{choreLabel(chores[d]!)}</div> : null}
          </button>
        );
      })}
    </Card>
  );
}

const MAX_TITLES = 3;

/** Fixed 6×7 month grid with up to three plan titles per day. */
export function MonthView({ anchor, plans, loading, onOpenDay }: GridProps) {
  const ctx = useDisplay();
  const today = ctx.clock.dateKey;
  const rows = monthGrid(anchor, ctx.prefs.week_starts);
  const heads = weekdayHeaders(ctx.prefs.week_starts);
  const { byDay, chores } = useDayIndex(plans);
  const portrait = ctx.orientation === 'portrait';
  if (loading) return <Card tone={5} className="mt-[14px] flex-1"><Unavailable>Loading the month…</Unavailable></Card>;
  return (
    <Card tone={5} className="mt-[14px] flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="grid flex-none border-b border-line" style={{ gridTemplateColumns: 'repeat(7, minmax(0,1fr))' }}>
        {heads.map((h) => (
          <div key={h} className="py-2 text-center text-[12px] uppercase tracking-[0.08em] text-muted">
            {h}
          </div>
        ))}
      </div>
      <div className="grid min-h-0 flex-1" style={{ gridTemplateColumns: 'repeat(7, minmax(0,1fr))', gridTemplateRows: 'repeat(6, minmax(0,1fr))' }}>
        {rows.flat().map((c, i) => {
          const list = byDay[c.date] || [];
          const isToday = c.date === today;
          const extra = list.length - MAX_TITLES;
          return (
            <button
              key={c.date}
              type="button"
              onClick={() => onOpenDay(c.date)}
              aria-label={`${c.date}: ${list.length} plans. Open day.`}
              className="tap flex min-h-0 min-w-0 flex-col items-stretch overflow-hidden px-[5px] pt-1 text-left"
              style={{
                borderLeft: i % 7 ? '1px solid var(--line2)' : 'none',
                borderTop: i >= 7 ? '1px solid var(--line2)' : 'none',
                opacity: c.inMonth ? 1 : 0.4,
              }}
            >
              <span className="flex items-center justify-between">
                <span
                  className="flex h-[24px] w-[24px] items-center justify-center rounded-full text-[13px]"
                  style={isToday ? { background: 'var(--ink)', color: 'var(--onInk)', fontWeight: 600 } : undefined}
                >
                  {c.day}
                </span>
                {chores[c.date] ? (
                  <span className="text-[11px] text-muted" aria-label={choreLabel(chores[c.date]!)}>
                    ✓{chores[c.date]}
                  </span>
                ) : null}
              </span>
              {portrait ? (
                <span className="mt-1 flex flex-wrap">
                  {list.slice(0, 6).map((p) => (
                    <span key={p.id} className="mb-[3px] mr-[3px] h-[7px] w-[7px] rounded-full" style={{ background: personColor(memberLabel(ctx, p.member_id).colorKey) }} />
                  ))}
                </span>
              ) : (
                list.slice(0, MAX_TITLES).map((p) => (
                  <span key={p.id} className="ellipsis mt-[2px] block h-[17px] flex-none rounded-[4px] px-1 text-[11px] leading-[17px]" style={{ background: 'var(--soft)', boxShadow: `inset 2px 0 0 ${personColor(memberLabel(ctx, p.member_id).colorKey)}` }}>
                    {p.title}
                  </span>
                ))
              )}
              {!portrait && extra > 0 ? <span className="mt-[2px] text-[11px] text-muted">+{extra} more</span> : null}
            </button>
          );
        })}
      </div>
    </Card>
  );
}
